import { AttachIcon, SendIcon } from '@diligentcorp/atlas-react-icons';
import {
  ErrorFloatingCard,
  GovmapGroupsListItem,
  LoadingButton,
  LoadingProgress,
  ViewLabelValue,
  ViewSection,
} from '@issuemanager/apps/client/components';
import { useUserContext } from '@issuemanager/apps/client/contexts';
import {
  useAllQueries,
  useMonitorViewIssueLoad,
} from '@issuemanager/apps/client/hooks';
import { IssueTimelineEventTypeEnum, IssueType } from '@issuemanager/constants';
import {
  Divider,
  Grid,
  IconButton,
  Link,
  Stack,
  Typography,
  styled,
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import UserDateTime from '../../components/dates/UserDateTime';
import { TruncatedText } from '../../components/truncated-text/truncated-text';
import { useLocalizedIssueTypes } from '../../features';
import { SLASwitch } from '../issue/components/form-details/sla-switch';
import { IssueSeverityChip } from '../issue/components/issue-severity-chip';
import { SlaBanner } from '../issue/components/sla-banner';
import ViewIssueUser from '../issue/components/user/ViewIssueUser';
import { useIssueStatusInfo } from '../issue/hooks/use-issue-status-info';
import { useIssueOutletContext } from '../issue/hooks/useIssueOutletContext';
import { CommentAttachmentsModal } from './components/comment-attachments-modal';
import ConfirmReassignmentRequestDialog from './components/confirm-reassignment-request-dialog';
import { IssueTimeline } from './components/issue-timeline';
import { ReadonlyDocuments } from './components/read-only-documents';
import { useHeaderButtons } from './hooks/use-header-buttons';
import { useIssueCommentForm } from './hooks/useIssueCommentForm';

const StyledContainer = styled(Stack)`
  width: 100%;
  flex-direction: row;
  justify-content: space-between;
  gap: 24px;
`;

const StyledLeftStack = styled(Stack)`
  flex: 3 1 0;
  width: auto;
  gap: 24px;
`;

const StyledRightStack = styled(Stack)`
  flex: 1 1 0;
  min-width: 300px;
`;

const StyledDetailsStack = styled(Stack)`
  background-color: white;
  padding: 24px 16px;
  border-radius: 8px;
  row-gap: 16px;
`;

const StyledDetailsRow = styled(Stack)`
  flex-direction: row;
  width: 100%;
  gap: 96px;
`;

const StyledTimelineStack = styled(Stack)`
  background-color: white;
  padding: 24px 16px;
  border-radius: 8px;
`;

const StyledDivider = styled(Divider)`
  width: 100%;
  margin: 16px 0;
`;

export default function ViewIssue(): React.JSX.Element {
  const { t } = useTranslation(['issue', 'common']);
  const { code, issue, userRoles: issueUserRoles } = useIssueOutletContext();
  const monitorViewIssueLoad = useMonitorViewIssueLoad();
  const { data: userData } = useUserContext();

  const [openReassignmentRequestModal, setOpenReassignmentRequestModal] =
    useState(false);
  const [openAddAttachmentsModal, setOpenAddAttachmentsModal] = useState(false);

  const commentForm = useIssueCommentForm(code);
  const localizedIssueTypes = useLocalizedIssueTypes();
  const { isClosedOrDiscarded } = useIssueStatusInfo({
    status: issue.data?.status,
    isInitiated: issue.data?.isInitiated,
    approvalStatus: issue.data?.approvalStatus,
  });

  const showSLAData = useMemo(() => {
    const issueData = issue.data;
    const issueUserRolesData = issueUserRoles.data;
    if (!issueData || !userData || !issueUserRolesData) return false;

    const isManager = userData.id === issueData.manager?.id;
    const isTypeOrSubtypeManager = issueUserRolesData.isIssueTypeManager;

    return (isManager || isTypeOrSubtypeManager) && issueData.slaDays;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [issue.dataUpdatedAt, userData?.updatedAt, issueUserRoles.dataUpdatedAt]);

  const showSLABanner = useMemo(() => {
    const issueData = issue.data;
    const issueUserRolesData = issueUserRoles.data;
    if (!issueData || !userData || !issueUserRolesData) {
      return { slaEventBanner: false, slaOverdueBanner: false };
    }
    const slaOverdue =
      issueData.slaDueDays &&
      issueData.slaDueDays < 0 &&
      issueData.slaEvent?.eventType !==
        IssueTimelineEventTypeEnum.SLADeactivated;
    const { slaEvent } = issueData;
    const isManager = userData.id === issueData.manager?.id;
    const isTypeOrSubtypeManager = issueUserRolesData.isIssueTypeManager;
    const canSeeSLA = isManager || isTypeOrSubtypeManager;

    if (!canSeeSLA) return { slaEventBanner: false, slaOverdueBanner: false };
    return {
      slaEventBanner: slaEvent && issueData.slaDays,
      slaOverdueBanner: slaOverdue,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [issue.dataUpdatedAt, userData?.updatedAt, issueUserRoles.dataUpdatedAt]);

  const handleCommentAttachmentsClose = (): void => {
    commentForm.form.resetField('attachments');
    setOpenAddAttachmentsModal(false);
  };

  const handleCommentAttachmentsAccept = (): void => {
    commentForm.form.resetField('attachments', {
      defaultValue: commentForm.form.getValues('attachments'),
    });
    setOpenAddAttachmentsModal(false);
  };

  useAllQueries({
    queries: [issue],
    onSuccess: () => {
      monitorViewIssueLoad.stop();
    },
    onError: () => {
      monitorViewIssueLoad.error();
    },
  });

  useHeaderButtons({
    onRequestReassignment: () => setOpenReassignmentRequestModal(true),
  });

  const canAttachOnComment = useMemo(() => {
    const issueUserRolesData = issueUserRoles.data;

    return Boolean(
      issueUserRolesData?.isManager ||
        issueUserRolesData?.isOwner ||
        issueUserRolesData?.isIssueTypeManager ||
        issueUserRolesData?.isCreator,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [issueUserRoles.dataUpdatedAt]);

  const label: string = useMemo(() => {
    const { name, entityType, id } = issue?.data?.source ?? {};
    return [name, entityType, id].filter(Boolean).join(' - ') || '';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [issue?.dataUpdatedAt]);

  return (
    <Stack margin={3} data-testid='view-issue'>
      {issue.isFetching ? (
        <LoadingProgress height={500} />
      ) : (
        <>
          {issue.data && (
            <Stack marginBottom={3} spacing={2}>
              {showSLABanner.slaEventBanner && <SlaBanner issue={issue.data} />}
              {showSLABanner.slaOverdueBanner && (
                <SlaBanner issue={issue.data} isOverdue />
              )}
            </Stack>
          )}
          <Typography variant='h4' fontWeight={600} marginBottom={3}>
            {t('issue:view.subtitle.details')}
          </Typography>
          <StyledContainer>
            <StyledLeftStack>
              <StyledDetailsStack data-testid='details'>
                <StyledDetailsRow>
                  <ViewLabelValue
                    label={t('issue:view.labels.issueType')}
                    value={[
                      localizedIssueTypes[
                        issue.data?.issueType?.name as IssueType
                      ] ?? issue.data?.issueType?.name,
                      issue?.data?.issueSubtype?.name,
                    ]
                      .filter(Boolean)
                      .join(' - ')}
                    testId='issue-type-name'
                  />
                  {issue?.data?.source && label && (
                    <Grid item xs={12}>
                      <ViewLabelValue
                        label={t('issue:view.labels.source')}
                        value={
                          issue.data.source.link ? (
                            <Link href={issue.data.source.link} target='_blank'>
                              <Typography fontWeight='bold' variant='inherit'>
                                {label}
                              </Typography>
                            </Link>
                          ) : (
                            <Typography fontWeight='bold' variant='inherit'>
                              {label}
                            </Typography>
                          )
                        }
                        testId='source'
                      />
                    </Grid>
                  )}
                </StyledDetailsRow>
                <Stack rowGap={2}>
                  <ViewLabelValue
                    label={t('issue:view.labels.description')}
                    value={
                      issue?.data?.description ? (
                        <TruncatedText
                          text={issue.data.description}
                          limit={150}
                        />
                      ) : (
                        '-'
                      )
                    }
                    testId='description'
                  />
                  <ViewLabelValue
                    label={t('issue:view.labels.impact')}
                    value={
                      issue?.data?.impact ? (
                        <TruncatedText text={issue.data.impact} limit={150} />
                      ) : (
                        '-'
                      )
                    }
                    testId='impact'
                  />
                  {issue?.data?.attachments &&
                  issue.data.attachments.length > 0 ? (
                    <ReadonlyDocuments attachments={issue.data.attachments} />
                  ) : (
                    <ViewLabelValue
                      label={t('issue:view.labels.attachments')}
                      value='-'
                      testId='attachments'
                    />
                  )}
                </Stack>
              </StyledDetailsStack>
              <Typography variant='h4' fontWeight={600}>
                {t('issue:view.subtitle.timeline')}
              </Typography>
              <StyledTimelineStack>
                {!isClosedOrDiscarded && (
                  <Stack direction='row' spacing={1}>
                    <Stack flex={6}>{commentForm.commentFields.comment}</Stack>
                    <Stack spacing={1} direction='row'>
                      <FormProvider {...commentForm.form}>
                        {canAttachOnComment && (
                          <IconButton
                            onClick={() => setOpenAddAttachmentsModal(true)}
                          >
                            <AttachIcon />
                          </IconButton>
                        )}
                        <LoadingButton
                          variant='contained'
                          startIcon={<SendIcon />}
                          color='primary'
                          onClick={commentForm.commentSubmit}
                          data-testid='add-comment-button'
                          loading={commentForm.isLoading}
                        >
                          {commentForm.isLoading
                            ? t('issue:comment.labels.sending')
                            : t('issue:comment.labels.send')}
                        </LoadingButton>
                        <CommentAttachmentsModal
                          attachmentsField={
                            commentForm.commentFields.attachments
                          }
                          open={openAddAttachmentsModal}
                          onClose={handleCommentAttachmentsClose}
                          onAccept={handleCommentAttachmentsAccept}
                          hasAttachments={
                            commentForm.form.getValues('attachments').length > 0
                          }
                        />
                      </FormProvider>
                    </Stack>
                  </Stack>
                )}
                <IssueTimeline timeline={issue.data?.timelineEvents} />
                {commentForm.hasErrors && (
                  <ErrorFloatingCard
                    onClose={commentForm.commentCleanErrors}
                    errors={commentForm.commentErrors}
                  />
                )}
              </StyledTimelineStack>
            </StyledLeftStack>
            <StyledRightStack>
              <ViewSection data-testid='sidebar'>
                {issue.isSuccess && issue.data && (
                  <Stack spacing={2}>
                    {showSLAData && (
                      <Stack>
                        <SLASwitch issue={issue.data} />
                        <StyledDivider />
                      </Stack>
                    )}
                    <ViewLabelValue
                      testId='dueDate'
                      label={t('issue:view.labels.dueDate')}
                      value={
                        issue.data?.dueDate ? (
                          <UserDateTime date={issue.data.dueDate} />
                        ) : (
                          '-'
                        )
                      }
                    />
                    <ViewLabelValue
                      label={t('issue:view.labels.lastActivity')}
                      value={
                        issue.data?.updatedAt ? (
                          <UserDateTime date={new Date(issue.data.updatedAt)} />
                        ) : (
                          '-'
                        )
                      }
                    />
                    <ViewLabelValue
                      label={t('issue:view.labels.severity')}
                      value={
                        issue.data?.severity ? (
                          <IssueSeverityChip severity={issue.data.severity} />
                        ) : (
                          '-'
                        )
                      }
                    />
                    {issue?.data?.govmapGroups &&
                      issue?.data?.govmapGroups?.length > 0 && (
                        <ViewLabelValue
                          label={t('issue:view.labels.relatedOrgUnit')}
                          value={issue.data.govmapGroups?.map(
                            (item, index, array) => (
                              <GovmapGroupsListItem
                                key={item.id}
                                name={item.name}
                                parentName={item.parentGroupName}
                                showSeparator={index !== array.length - 1}
                              />
                            ),
                          )}
                        />
                      )}
                    <StyledDivider />
                    <ViewIssueUser
                      labelText={t('issue:view.labels.creatorUser')}
                      name={issue.data.creator?.name}
                      testId='issue-creator'
                      initials={issue.data.creator?.initials}
                    />
                    <ViewLabelValue
                      label={t('issue:view.labels.created')}
                      value={
                        <UserDateTime date={new Date(issue.data.createdAt)} />
                      }
                    />
                    <ViewLabelValue
                      label={t('issue:view.labels.actualRemediationDate')}
                      value={
                        issue.data.actualRemediationDate ? (
                          <UserDateTime
                            date={issue.data.actualRemediationDate}
                          />
                        ) : (
                          '-'
                        )
                      }
                    />
                    <StyledDivider />
                    <ViewIssueUser
                      labelText={t('issue:view.labels.managerUser')}
                      name={issue.data.manager?.name}
                      testId='issue-manager'
                      initials={issue.data.manager?.initials}
                    />
                    <ViewIssueUser
                      labelText={t('issue:view.labels.ownerUser')}
                      name={issue.data.owner?.name}
                      testId='issue-owner'
                      initials={issue.data.owner?.initials}
                    />
                    <ViewIssueUser
                      labelText={t('issue:view.labels.approver')}
                      name={issue.data.approver?.name}
                      testId='approver'
                      initials={issue.data.owner?.initials}
                    />
                  </Stack>
                )}
              </ViewSection>
            </StyledRightStack>
          </StyledContainer>
          <ConfirmReassignmentRequestDialog
            open={openReassignmentRequestModal}
            onClose={() => setOpenReassignmentRequestModal(false)}
          />
        </>
      )}
    </Stack>
  );
}
